import { useRef } from "react";

import { isStructurallyEqual } from "./useStableValue.logic";

/**
 * Keeps a derived value's identity for as long as its content is unchanged.
 *
 * A thread's activities grow with every provider delta, so anything derived
 * from them answers with a fresh object at stream frequency even when nothing
 * it describes moved. Components downstream are `memo`ed on identity, and a
 * composer re-rendered at stream frequency is what makes typing feel laggy;
 * this is what lets that memo hold.
 *
 * Only for small plain values — the equality walk is structural. Never pass
 * React nodes: a development element's owner fiber is cyclic, and the walk
 * recurses until the stack overflows.
 *
 * The ref is advanced during render, which a discarded render can reach first.
 * That is safe here because it only ever advances to a value the next render
 * would find structurally equal, so every reader sees the content it asked for.
 */
export function useStableValue<A>(value: A): A {
  const stable = useRef(value);
  if (!isStructurallyEqual(stable.current, value)) {
    stable.current = value;
  }
  return stable.current;
}
